import { useMemo, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { AI_MODEL_OFF, isAiModelOff, type AppSetting } from '@peles/shared';
import { useAiFeatures, useBedrockModels } from '../../hooks/useAi';
import { useCreateAppSetting, useUpdateAppSetting } from '../../hooks/useAppSettings';

const GRAMMAR_ENABLED_CODE = 'ai_grammar_enabled';
const MODEL_LIST_ID = { text: 'bedrock-model-options', image: 'bedrock-image-model-options' };

/**
 * Configure the AWS Bedrock model used per AI flow. Each field is a free-text
 * model id with a live datalist of the account's models — so an admin can pick
 * a listed one or paste any id or inference-profile ARN. Blank falls back to the
 * flow's default. Plugs into the App Settings tab (no separate admin page).
 *
 * ⚠️ **The rows come from `GET /ai/features`, not from a constant here.** The
 * backend registry is what actually resolves a model and bills a call; a second
 * list in the frontend that had to agree with it failed silently when it did
 * not — a picker writing `ai_model_x` that nothing reads. So a flow added on
 * the server appears here with no frontend change, and one that does not exist
 * cannot be configured.
 *
 * ⚠️ **This is our markup, not `<AiModelsConsole>`.** CLAUDE.md's line: a
 * screen that *is* the package's renders whole, and this is a card inside the
 * App Settings tab, beside settings drawn by our own components. What was worth
 * adopting was the registry behind it, and that is what was adopted.
 */
export function AiModelsSection({ settings }: { settings: AppSetting[] }) {
  const { data: models = [] } = useBedrockModels();
  const { data: features = [], isLoading: featuresLoading } = useAiFeatures();
  const updateSetting = useUpdateAppSetting();
  const createSetting = useCreateAppSetting();

  const textModels = useMemo(() => models.filter((m) => m.output !== 'image'), [models]);
  const imageModels = useMemo(() => models.filter((m) => m.output === 'image'), [models]);

  const settingByCode = useMemo(() => new Map(settings.map((s) => [s.varCode, s])), [settings]);

  const grammarSetting = settingByCode.get(GRAMMAR_ENABLED_CODE);
  const grammarEnabled = grammarSetting ? grammarSetting.value !== 'false' : true;

  async function persist(
    varCode: string,
    name: string,
    description: string,
    value: string | null,
    varType: 'TEXT' | 'BOOLEAN',
  ) {
    const existing = settingByCode.get(varCode);
    if (existing) {
      await updateSetting.mutateAsync({ id: existing.id, updates: { value } });
    } else {
      await createSetting.mutateAsync({
        varCode,
        name,
        description,
        varType,
        ...(value !== null ? { value } : {}),
      });
    }
  }

  async function toggleGrammar() {
    await persist(
      GRAMMAR_ENABLED_CODE,
      'AI Grammar & Spelling',
      'Enables the grammar and translation assistants in the email/message composer.',
      grammarEnabled ? 'false' : 'true',
      'BOOLEAN',
    );
  }

  return (
    <div className="p-4 border-b border-gray-200 bg-gray-50/60">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles size={16} className="text-primary-600" />
        <h3 className="text-sm font-semibold text-gray-900">AI Models</h3>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        Bedrock model used per AI function. Pick from the list (foundation models and{' '}
        <code>us.*</code> inference profiles) or paste any model id / inference-profile ARN. Newer
        models must use their inference-profile id. Leave blank to use the default model.
      </p>

      {/* Two lists: a flow that draws pictures is offered image models, one
          that writes text is offered text models — picking across is a model
          the flow cannot drive. Free text still takes any id or ARN. */}
      <datalist id={MODEL_LIST_ID.text}>
        {textModels.map((m) => (
          <option key={m.modelId} value={m.modelId}>
            {[m.providerName, m.modelName].filter(Boolean).join(' · ')}
          </option>
        ))}
      </datalist>
      <datalist id={MODEL_LIST_ID.image}>
        {imageModels.map((m) => (
          <option key={m.modelId} value={m.modelId}>
            {[m.providerName, m.modelName].filter(Boolean).join(' · ')}
          </option>
        ))}
      </datalist>

      <label className="flex items-center gap-2 text-sm text-gray-700 mb-4">
        <input
          type="checkbox"
          checked={grammarEnabled}
          onChange={() => void toggleGrammar()}
          disabled={updateSetting.isPending || createSetting.isPending}
          className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          data-testid="ai-grammar-enabled"
        />
        Enable the writing assistant (grammar and translation) in the message composer
      </label>

      <div className="space-y-3">
        {featuresLoading && <p className="text-xs text-gray-400">Loading AI flows…</p>}
        {!featuresLoading && features.length === 0 && (
          // No local fallback list: one that could disagree with the server is
          // exactly the drift this section stopped having.
          <p className="text-xs text-gray-400" data-testid="ai-features-unavailable">
            The AI flows could not be loaded. Reload to try again.
          </p>
        )}
        {features.map((fn) => (
          <AiModelRow
            key={fn.key}
            label={fn.label}
            description={fn.description}
            varCode={fn.settingVarCode}
            listId={fn.modality === 'image' ? MODEL_LIST_ID.image : MODEL_LIST_ID.text}
            canTurnOff={fn.canTurnOff === true}
            currentValue={settingByCode.get(fn.settingVarCode)?.value ?? ''}
            onSave={(value) =>
              persist(
                fn.settingVarCode,
                `AI Model — ${fn.label}`,
                fn.description,
                value || null,
                'TEXT',
              )
            }
            saving={updateSetting.isPending || createSetting.isPending}
          />
        ))}
      </div>
    </div>
  );
}

function AiModelRow({
  label,
  description,
  varCode,
  listId,
  canTurnOff = false,
  currentValue,
  onSave,
  saving,
}: {
  label: string;
  description: string;
  varCode: string;
  listId: string;
  /** The flow is optional: its work goes ahead without it when it is off (`AI_MODEL_OFF`). */
  canTurnOff?: boolean;
  currentValue: string;
  onSave: (value: string) => Promise<void>;
  saving: boolean;
}) {
  const [draft, setDraft] = useState(currentValue);
  const dirty = draft.trim() !== currentValue.trim();
  const off = canTurnOff && isAiModelOff(currentValue);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-[220px_1fr_auto] gap-2 sm:items-center">
      <div>
        <p className="text-sm font-medium text-gray-800">{label}</p>
        <p className="text-xs text-gray-400">{description}</p>
      </div>
      <input
        type="text"
        list={listId}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="Default model"
        aria-label={`Model for ${label}`}
        className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        data-testid={`ai-model-input-${varCode}`}
      />
      <button
        type="button"
        onClick={() => void onSave(draft.trim())}
        disabled={!dirty || saving}
        className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed"
        data-testid={`ai-model-save-${varCode}`}
      >
        Save
      </button>
      {canTurnOff && (
        // An optional flow is switched off by its setting reading `off`, so the
        // model it falls back to on "Turn on" is whatever the default is then.
        <div className="sm:col-start-2 sm:col-span-2 flex items-center gap-2 text-xs text-gray-500">
          {off ? (
            <span data-testid={`ai-model-off-${varCode}`}>Off — sent without it.</span>
          ) : (
            <span>Optional: turn it off to go ahead without it.</span>
          )}
          <button
            type="button"
            onClick={() => {
              const next = off ? '' : AI_MODEL_OFF;
              setDraft(next);
              void onSave(next);
            }}
            disabled={saving}
            className="text-primary-600 hover:underline disabled:opacity-40"
            data-testid={`ai-model-toggle-off-${varCode}`}
          >
            {off ? 'Turn on' : 'Turn off'}
          </button>
        </div>
      )}
    </div>
  );
}
